import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import {
  ChatBubbleLeftRightIcon,
  DevicePhoneMobileIcon,
  EnvelopeIcon,
} from "@heroicons/react/24/outline";
import Tabs from "../../../../shared/Tabs";
import Button from "../../../../shared/Button";
import Notes from "./Notes";
import { usePermissions } from "../../../../hooks/usePermissions";
import { PERMISSIONS } from "../../../../constants/access-control";
import { useJob, useAuth, useConfig } from "../../../../hooks";
import { useGetEntityTimelineQuery } from "../../../../redux/services/timeline/api";
import { useGetJobMediaQuery } from "../../../../redux/services/jobs/jobsApi";
import { useSendEmailMutation } from "../../../../redux/services/email/emailApi";
import CommunicationsTimeline from "@/components/CommunicationsTimeline";
import { toast } from "react-toastify";
import { AttachmentsSidebarList } from "@/components";
import {
  useCreateGroupConversationMutation,
  useGetChatConversationsQuery,
  useGetConversationMessagesQuery,
  useGetOrCreateDmConversationMutation,
  useMarkConversationReadMutation,
  useSendConversationMessageMutation,
} from "@/redux/services/chat/chatApi";
import { useChatSocket } from "@/contexts/ChatContext";
import { getLatestOutboundReceiptState } from "@/redux/services/chat/readReceipts";

const SidebarContent = ({ form, updateForm, onFormChange, ...props }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { configs } = useConfig();
  const { joinConversation, leaveConversation, isConnected } = useChatSocket();
  const { hasPermission } = usePermissions();
  const canEditJobs = hasPermission(PERMISSIONS.JOBS_EDIT);
  const { data: job } = useJob();
  const {
    data: timeline,
    isLoading: timelineLoading,
    isFetching,
  } = useGetEntityTimelineQuery(job?._id);

  // Fetch media/attachments for the job
  const { data: mediaData, isLoading: isLoadingMedia } = useGetJobMediaQuery(
    { id: job?._id },
    { skip: !job?._id }
  );

  // Communication state
  const [communicationType, setCommunicationType] = useState("text");
  const [messageContent, setMessageContent] = useState("");
  const [subject, setSubject] = useState("");
  const [sendEmail, { isLoading: isSendingEmail }] = useSendEmailMutation();
  const [conversationId, setConversationId] = useState(null);

  const legsById = useMemo(() => {
    const legs = Array.isArray(job?.legs) ? job.legs : [];
    return legs.reduce((acc, leg, index) => {
      const key = String(leg?._id || "");
      if (!key) return acc;
      acc[key] = {
        ...leg,
        index: index + 1,
      };
      return acc;
    }, {});
  }, [job?.legs]);

  const getLegLabel = (leg) => {
    if (!leg) return "Unknown";
    if (leg.loadNumber) return `Load ${leg.loadNumber}`;
    if (leg.orderNumber) return `Order ${leg.orderNumber}`;
    return `Leg ${leg.index || ""}`.trim();
  };

  const jobContext = useMemo(() => {
    if (!job?._id) return null;
    return { entityType: "job", entityId: job._id };
  }, [job?._id]);

  const { data: chatConversationsResp } = useGetChatConversationsQuery(
    jobContext
      ? { contextEntityType: jobContext.entityType, contextEntityId: jobContext.entityId }
      : {},
    {
      skip: !jobContext,
      pollingInterval: 10000,
      refetchOnReconnect: true,
      refetchOnFocus: true,
    }
  );
  const chatConversations = chatConversationsResp?.data || [];

  useEffect(() => {
    if (!chatConversations?.length) return;
    // Prefer group conversation for job context; fallback to the first
    const group = chatConversations.find((c) => c.type === "group");
    setConversationId((group || chatConversations[0])?._id || null);
  }, [chatConversationsResp?.data?.length, job?._id]);

  const { data: chatMessagesResp } = useGetConversationMessagesQuery(
    { conversationId, limit: 100 },
    {
      skip: !conversationId,
      pollingInterval: isConnected ? 0 : 5000,
      refetchOnReconnect: true,
      refetchOnFocus: true,
    }
  );
  const chatMessages = chatMessagesResp?.data || chatMessagesResp || [];

  const [createGroupConversation] = useCreateGroupConversationMutation();
  const [getOrCreateDmConversation] = useGetOrCreateDmConversationMutation();
  const [markConversationRead] = useMarkConversationReadMutation();
  const [sendChatMessage, { isLoading: isSendingChat }] =
    useSendConversationMessageMutation();

  const activeChatConversation = useMemo(() => {
    if (!conversationId) return null;
    return chatConversations?.find((c) => String(c._id) === String(conversationId)) || null;
  }, [conversationId, chatConversations?.length]);

  const activeSidebarTabId = searchParams.get("job-comms") || "Communications";
  const { latestOutboundMessageId: latestOutboundChatMessageId, readReceiptLabel } =
    useMemo(
      () =>
        getLatestOutboundReceiptState({
          conversation: activeChatConversation,
          messages: chatMessages,
          currentUserId: user?._id,
        }),
      [activeChatConversation, chatMessages, user?._id]
    );

  useEffect(() => {
    if (activeSidebarTabId !== "Communications" || !conversationId) return;
    if (isConnected) {
      joinConversation?.(conversationId);
    }

    return () => {
      leaveConversation?.(conversationId);
    };
  }, [
    activeSidebarTabId,
    conversationId,
    isConnected,
    joinConversation,
    leaveConversation,
  ]);

  useEffect(() => {
    if (activeSidebarTabId !== "Communications" || !conversationId) return;
    if (!chatMessages?.length) return;
    if (String(chatMessages[chatMessages.length - 1]?.author?._id) === String(user?._id)) {
      return;
    }

    markConversationRead({ conversationId }).catch(() => {});
  }, [
    activeSidebarTabId,
    conversationId,
    chatMessages,
    markConversationRead,
    user?._id,
  ]);

  const chatRecipientNames = useMemo(() => {
    // Prefer the assigned driver(s)/resources on the job for "who we're messaging"
    const assigned = Array.isArray(job?.assignedTo)
      ? job.assignedTo
      : job?.assignedTo
      ? [job.assignedTo]
      : [];

    const assignedNames = assigned
      .map((p) => p?.fullName)
      .filter(Boolean);

    if (assignedNames.length) return assignedNames.join(", ");

    // Fallback: conversation participants excluding current user
    const participants = activeChatConversation?.participants || [];
    const otherNames = participants
      .filter((p) => String(p?._id || p) !== String(user?._id))
      .map((p) => p?.fullName)
      .filter(Boolean);

    return otherNames.length ? otherNames.join(", ") : "Unassigned";
  }, [job?.assignedTo, activeChatConversation?._id, user?._id]);

  const timelineItems = useMemo(() => {
    if (!job) return [];

    // Always show job creation
    const baseTimeline = [
      {
        id: "job-created",
        timestamp: job?.dateCreated && new Date(job.dateCreated).toISOString(),
        sender: { name: "System" },
        title: "Job Created",
        description: "Job was created",
        type: "update",
      },
    ];

    // Add timeline items if available
    if (!timeline?.length) return baseTimeline;

    const additionalItems = timeline.map((item) => {
      const baseItem = {
        id: item._id,
        timestamp: new Date(item.timestamp).toISOString(),
        sender: {
          name: item.createdBy?.name || "System",
        },
      };

      switch (item.type) {
        case "quoteSent":
          return {
            ...baseItem,
            title: "Quote Sent",
            description: `Quote sent to ${item.data.recipients.join(", ")}`,
            type: "quote",
          };
        case "proposalSent":
          return {
            ...baseItem,
            title: "Proposal Sent",
            description: `${item.data.subject} to ${item.data.recipients.join(
              ", "
            )}`,
            type: "proposal",
          };
        case "jobStatus":
          return {
            ...baseItem,
            title: "Status Update",
            description:
              item.data.description ||
              `${item.data.reason} to ${item.data.new}`,
            type: "status",
          };
        case "approval":
          return {
            ...baseItem,
            title: "Approval Update",
            description: `${
              item.data.status === "approved" ? "Approved" : "Rejected"
            } by ${
              item.data.reviewedBy?.type !== "client"
                ? item.data.reviewedBy?.name
                : item.data.signature
            }`,
            type: "approval",
            sender: { name: item.data.reviewedBy.name },
            metadata: {
              status: {
                type: "update",
                content: item.data.status,
                hidden: true,
              },
              notes: {
                type: "message",
                isOutbound: item.data.reviewedBy?.type !== "client",
                content: item.data.notes,
              },
            },
          };
        case "note":
          return {
            ...baseItem,
            title: "Note",
            description: item.data.content,
            type: "message",
            isOutbound: true,
          };
        case "communication":
          return {
            ...baseItem,
            title: item.data.channel || "Communication",
            description: item.data.content,
            type: "message",
            isOutbound: item.data.direction === "outbound",
          };
        default:
          return {
            ...baseItem,
            title: item.type,
            description:
              typeof item.data === "string"
                ? item.data
                : JSON.stringify(item.data),
            type: "update",
          };
      }
    });

    // Convert chat messages into timeline items so they appear in the same sidebar feed
    const chatItems = (chatMessages || []).map((m) => ({
      id: m._id,
      timestamp: new Date(m.dateCreated || m.createdAt || Date.now()).toISOString(),
      sender: { name: m.author?.fullName || "User" },
      title: "Chat",
      description: m.content,
      type: "message",
      isOutbound: String(m.author?._id) === String(user?._id),
      readReceiptLabel:
        readReceiptLabel && String(m._id) === String(latestOutboundChatMessageId)
          ? readReceiptLabel
          : undefined,
    }));

    return [...baseTimeline, ...additionalItems, ...chatItems].sort(
      (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
    );
  }, [
    job,
    timeline,
    timeline?.length,
    chatMessages,
    user?._id,
    latestOutboundChatMessageId,
    readReceiptLabel,
  ]);

  // Get email addresses
  const driverEmails = useMemo(() => {
    if (!job?.assignedTo) return [];
    const assigned = Array.isArray(job.assignedTo)
      ? job.assignedTo
      : [job.assignedTo];
    return assigned
      .map((d) => d?.email)
      .filter((email) => email);
  }, [job?.assignedTo]);

  const clientEmail = job?.client?.email;

  const getAssignedParticipantIds = () => {
    // Primary recipients should be the assigned driver/tech on the job/legs.
    const assigned = Array.isArray(job?.assignedTo)
      ? job.assignedTo
      : job?.assignedTo
      ? [job.assignedTo]
      : [];
    return assigned.map((a) => String(a?._id)).filter(Boolean);
  };

  const ensureJobChatConversation = async () => {
    if (conversationId) return conversationId;
    if (!job?._id) return null;

    // If the context already exists in list, use it
    if (chatConversations?.length) {
      const group = chatConversations.find((c) => c.type === "group");
      const foundId = (group || chatConversations[0])?._id;
      if (foundId) {
        setConversationId(foundId);
        return foundId;
      }
    }

    const assignedIds = getAssignedParticipantIds();
    const otherIds = assignedIds.filter((id) => String(id) !== String(user?._id));

    // If we only have one assigned participant, use a DM for this job context
    if (otherIds.length === 1) {
      const dm = await getOrCreateDmConversation({
        otherUserId: otherIds[0],
        context: jobContext,
      }).unwrap();
      const id = dm?.data?._id;
      if (id) setConversationId(id);
      return id;
    }

    // If multiple assigned participants exist, create a job-scoped group conversation
    if (otherIds.length > 1) {
      const group = await createGroupConversation({
        participants: otherIds, // controller adds current user
        title: `Job ${job?.jobId || job?._id}`,
        context: jobContext,
      }).unwrap();

      const id = group?.data?._id;
      if (id) setConversationId(id);
      return id;
    }

    // Fallback: no assigned participants (unassigned job)
    const group = await createGroupConversation({
      participants: [], // controller adds current user
      title: `Job ${job?.jobId || job?._id}`,
      context: jobContext,
    }).unwrap();

    const id = group?.data?._id;
    if (id) setConversationId(id);
    return id;
  };

  // Handle sending communication
  const handleSendCommunication = async () => {
    if (!messageContent.trim()) {
      toast.error("Please enter a message");
      return;
    }

    try {
      if (communicationType === "text") {
        // Internal job chat (connected to the global messenger feature)
        const convoId = await ensureJobChatConversation();
        if (!convoId) {
          toast.error("Unable to start job chat");
          return;
        }
        await sendChatMessage({
          conversationId: convoId,
          content: messageContent,
        }).unwrap();
        setMessageContent("");
      } else {
        const recipients =
          communicationType === "email_driver" ? driverEmails : [clientEmail];

        await sendEmail({
          to: recipients,
          subject: subject || `Regarding Job #${job?.jobId || job?._id}`,
          body: messageContent,
          jobId: job?._id,
        }).unwrap();

        toast.success("Email sent successfully");
        setMessageContent("");
        setSubject("");
      }
    } catch (error) {
      console.error("Failed to send communication:", error);
      toast.error("Failed to send communication");
    }
  };

  // Only show loading if we have no job data
  const isLoading = !job;

  /**
   * Feature flags (default allow when missing for backwards compatibility)
   * Only treat a flag as disabled when it is explicitly set to `false`.
   */
  const featureFlags = configs?.featureFlags || {};
  const isCommunicationsEnabled =
    featureFlags?.["jobs.communications.enabled"] !== false;

  // Render Communications Tab
  const renderCommunicationsTab = () => {
    return (
      <div className="flex flex-col" style={{ height: "100%" }}>
        {/* Timeline */}
        <div className="flex-1 overflow-y-auto p-3" style={{ minHeight: 0 }}>
          <CommunicationsTimeline items={timelineItems} loading={isLoading} />
        </div>

        {/* Send Communication Form */}
        <div className="flex-shrink-0 border-t border-gray-200 p-3 space-y-3 bg-white">
          <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
            Send Communication
          </h3>

          {/* Communication Type Selector */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setCommunicationType("text")}
              className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg border transition-colors text-xs cursor-pointer ${
                communicationType === "text"
                  ? "bg-teal-50 border-teal-500 text-teal-700"
                  : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              <DevicePhoneMobileIcon className="h-3.5 w-3.5" />
              <span className="font-medium">Chat</span>
            </button>
            <button
              type="button"
              onClick={() => setCommunicationType("email_driver")}
              className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg border transition-colors text-xs cursor-pointer ${
                communicationType === "email_driver"
                  ? "bg-teal-50 border-teal-500 text-teal-700"
                  : driverEmails.length === 0
                  ? "bg-gray-50 border-gray-200 text-gray-400"
                  : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
              title={
                driverEmails.length === 0
                  ? "No driver email available"
                  : "Email Driver"
              }
            >
              <EnvelopeIcon className="h-3.5 w-3.5" />
              <span className="font-medium">Driver</span>
            </button>
            <button
              type="button"
              onClick={() => setCommunicationType("email_client")}
              className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg border transition-colors text-xs cursor-pointer ${
                communicationType === "email_client"
                  ? "bg-teal-50 border-teal-500 text-teal-700"
                  : !clientEmail
                  ? "bg-gray-50 border-gray-200 text-gray-400"
                  : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
              title={!clientEmail ? "No client email available" : "Email Client"}
            >
              <EnvelopeIcon className="h-3.5 w-3.5" />
              <span className="font-medium">Client</span>
            </button>
          </div>

          {/* Warning for missing email */}
          {communicationType === "email_client" && !clientEmail && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2">
              <p className="text-xs text-yellow-800">
                ⚠️ Client email not found
              </p>
            </div>
          )}
          {communicationType === "email_driver" && driverEmails.length === 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2">
              <p className="text-xs text-yellow-800">
                ⚠️ No driver email found
              </p>
            </div>
          )}

          {/* Recipient preview for internal chat */}
          {communicationType === "text" && (
            <div className="bg-secondary-50 border border-secondary-200 rounded-lg px-3 py-2">
              <p className="text-xs text-secondary-800">
                Chat with: <span className="font-semibold">{chatRecipientNames}</span>
              </p>
            </div>
          )}

          {/* Subject (for emails) */}
          {(communicationType === "email_driver" ||
            communicationType === "email_client") && (
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject..."
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:outline-none"
            />
          )}

          {/* Message Content */}
          <textarea
            value={messageContent}
            onChange={(e) => setMessageContent(e.target.value)}
            placeholder={
              communicationType === "text"
                ? "Enter chat message..."
                : "Enter email message..."
            }
            rows={3}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:outline-none resize-none"
          />

          {/* Send Button */}
          <Button
            onClick={handleSendCommunication}
            loading={isSendingEmail || isSendingChat}
            className="w-full"
            disabled={
              !messageContent.trim() ||
              (communicationType === "email_driver" &&
                driverEmails.length === 0) ||
              (communicationType === "email_client" && !clientEmail)
            }
            type="primary"
            size="sm"
          >
            <ChatBubbleLeftRightIcon className="h-4 w-4 mr-1.5" />
            Send{" "}
            {communicationType === "text"
              ? "Chat"
              : communicationType === "email_driver"
              ? "to Driver"
              : "to Client"}
          </Button>
        </div>
      </div>
    );
  };

  // Render Attachments Tab
  const renderAttachmentsTab = () => {
    const allAttachments = mediaData || [];
    return (
      <div className="p-3">
        <AttachmentsSidebarList
          attachments={allAttachments}
          isLoading={isLoadingMedia}
          emptyMessage="Files attached to this job will appear here"
          legsById={legsById}
          getLegLabel={getLegLabel}
        />
      </div>
    );
  };

  // Render Notes Tab
  const renderNotesTab = () => {
    return (
      <div className="p-3">
        <Notes job={job} form={form} onFormChange={onFormChange} />
      </div>
    );
  };

  function handleTabClick(_e, _item, path) {
    navigate(path);
  }

  // Build tabs array with current state
  // Not memoized because content depends on frequently changing state
  const tabs = [
    {
      id: "Communications",
      content: renderCommunicationsTab(),
      hidden: !isCommunicationsEnabled,
    },
    {
      id: "Attachments",
      content: renderAttachmentsTab(),
    },
    {
      id: "Notes",
      content: renderNotesTab(),
      hidden: !canEditJobs,
    },
  ];

  // Filter visible tabs
  const visibleTabs = tabs.filter((item) => !item.hidden);

  return (
    <Tabs
      className="w-full h-full flex flex-col"
      style={props.wrapperStyle}
      panelStyle={{
        ...props.panelStyle,
        height: props.panelStyle?.height || "100%",
      }}
      useSearchParams
      panelClasses="flex-1 border-t bg-gray-50 overflow-y-auto"
      panelItemClasses="h-full"
      tabListClasses="bg-white px-2 h-[38px] flex-shrink-0"
      tabclassName="mx-1"
      id="job-comms"
      onClick={handleTabClick}
      data={visibleTabs}
      scrollable
      fullHeight
    />
  );
};

export default SidebarContent;
